import React, { useCallback, useEffect, useState } from 'react';
import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import '../../college-theme.css';
import SharedDeskChrome from './SharedDeskChrome';

// The College Desk as somebody else sees it. Same shape as the student's own
// shell — a banner saying whose desk this is, the Desk header, and a tab row —
// except the tabs are whatever their access actually covers.
//
// The desk payload is loaded once here and handed down: the overview, the
// deadlines and the task list are three readings of the same thing, so they
// shouldn't be three requests.
export default function SharedDeskShell() {
  const { grantId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { api, user } = useAuth();

  const [desk, setDesk] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(
    () =>
      api
        .get(`/college-access/shared/${grantId}`)
        .then((res) => setDesk(res.data))
        .catch((err) => {
          toast.error(err.response?.data?.error || 'That desk is not shared with you');
          navigate('/shared', { replace: true });
        }),
    [api, grantId, navigate]
  );

  useEffect(() => {
    let alive = true;
    load().finally(() => {
      if (alive) setLoading(false);
    });
    return () => {
      alive = false;
    };
  }, [load]);

  if (loading) {
    return (
      <div className="college-scope" style={{ minHeight: '100vh', padding: '44px 32px' }}>
        <p className="font-mono-editorial" style={{ fontSize: 13, color: 'var(--college-muted)' }}>
          Opening…
        </p>
      </div>
    );
  }
  if (!desk) return null;

  const { grant } = desk;
  const base = `/shared/${grantId}`;
  const openTasks = (desk.myTasks || []).filter((t) => t.status !== 'DONE' && t.status !== 'DECLINED');

  // A tab only appears when the access behind it does.
  const tabs = [
    { label: 'Overview', path: base },
    ...(grant.seeList ? [{ label: 'My List', path: `${base}/list` }] : []),
    ...(grant.seeProgress ? [{ label: 'Essays', path: `${base}/essays` }] : []),
    ...(grant.seeList ? [{ label: 'Deadlines', path: `${base}/deadlines` }] : []),
    { label: 'My tasks', path: `${base}/tasks` },
  ];

  const activeTab =
    tabs.filter((t) => t.path !== base).find((t) => location.pathname.startsWith(t.path))?.label || 'Overview';

  return (
    <SharedDeskChrome
      grant={grant}
      viewerName={user?.name}
      tabs={{
        items: tabs.map((t) => t.label),
        active: activeTab,
        onChange: (label) => navigate(tabs.find((t) => t.label === label).path),
        badges: { 'My tasks': openTasks.length },
      }}
    >
      <Outlet context={{ grant, grantId, desk, setDesk, reloadDesk: load }} />
    </SharedDeskChrome>
  );
}
